import { useState, useEffect } from 'react';
import { Sparkles, Check, Coins, Crown, Zap, Image as ImageIcon, Music, Mic, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { EFFECT_PACKS, CARD_PACKS, AVATARS_PRESET, BACKGROUNDS, COACHES } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { shopApi } from '../lib/api';

export default function Shop() {
  const { user, requireAuth, updateUser } = useAuth();
  const [owned, setOwned] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    if (user) {
      shopApi.owned()
        .then((items) => setOwned(items.map((p) => p.item_id)))
        .catch(() => {});
    }
  }, [user]);

  const buy = async (item, type, priceEur = 0, coinsCost = 0) => {
    if (!requireAuth('register')) {
      toast.info('Connecte-toi pour acheter ✨');
      return;
    }
    if (owned.includes(item.id)) { toast.info('Déjà dans ta collection'); return; }
    if (coinsCost > 0 && user.coins < coinsCost) {
      toast.error('Crédits insuffisants. Recharge !');
      return;
    }
    setLoadingId(item.id);
    try {
      await shopApi.buy({ item_id: item.id, item_type: type, price_eur: priceEur, coins_cost: coinsCost });
      setOwned([...owned, item.id]);
      if (coinsCost > 0) updateUser({ coins: user.coins - coinsCost });
      toast.success(`✨ ${item.name || item.emoji} débloqué !`);
    } catch {
      toast.error('Achat échoué');
    } finally { setLoadingId(null); }
  };

  const topup = async (coinsAmt, priceEur) => {
    if (!requireAuth('register')) {
      toast.info('Connecte-toi pour recharger ✨');
      return;
    }
    setLoadingId(`topup_${coinsAmt}`);
    try {
      const r = await shopApi.topup({ coins: coinsAmt, price_eur: priceEur });
      updateUser({ coins: r.coins });
      toast.success(`+${coinsAmt} crédits !`, { description: `Paiement de ${priceEur}€ réussi (démo)` });
    } catch {
      toast.error('Recharge échouée');
    } finally { setLoadingId(null); }
  };

  const coins = user?.coins ?? 0;

  return (
    <div className="relative pt-24 pb-12 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <Badge className="bg-yellow-500/15 text-yellow-300 border-0 mb-2 px-3 py-1 rounded-full">Boutique</Badge>
            <h1 className="text-4xl sm:text-5xl font-black">Pimente ton <span className="text-gradient">style</span></h1>
            <p className="text-zinc-400 mt-2">Effets, fonds, avatars, cartes : exprime-toi sans limite.</p>
          </div>
          <div className="glass rounded-2xl px-5 py-3 flex items-center gap-3">
            <Coins className="text-yellow-400" size={22} />
            <div>
              <div className="text-xs text-zinc-400">Tes crédits</div>
              <div className="text-2xl font-black text-white">{coins}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { coins: 100, price: 0.99, label: '0,99€', popular: false },
            { coins: 500, price: 4.99, label: '4,99€', popular: true },
            { coins: 1200, price: 9.99, label: '9,99€', popular: false, bonus: '+200 bonus' },
            { coins: 3000, price: 19.99, label: '19,99€', popular: false, bonus: '+600 bonus' },
          ].map((p) => (
            <button key={`pack-${p.coins}`} onClick={() => topup(p.coins, p.price)} disabled={loadingId === `topup_${p.coins}`}
              className={`glass rounded-2xl p-4 card-hover text-left relative ${p.popular ? 'border-pink-500/40' : ''}`}>
              {p.popular && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full">MEILLEUR PRIX</div>
              )}
              {loadingId === `topup_${p.coins}` ? <Loader2 className="animate-spin text-pink-400 mb-2" size={28} /> : <Coins className="text-yellow-400 mb-2" size={28} />}
              <div className="text-2xl font-black text-white">{p.coins}</div>
              {p.bonus && <div className="text-xs text-green-400 font-semibold">{p.bonus}</div>}
              <div className="text-pink-400 font-bold mt-1">{p.label}</div>
            </button>
          ))}
        </div>

        <div className="glass rounded-3xl p-6 md:p-8 mb-10 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-purple-600/15 to-blue-500/20 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="text-yellow-400" size={26} />
                <span className="text-yellow-400 font-black text-sm">MUZI PASS</span>
              </div>
              <h3 className="text-3xl font-black mb-2">Tout illimité</h3>
              <p className="text-zinc-300 mb-4">Tous les effets, fonds, avatars, chansons IA, sans limite.</p>
              <ul className="space-y-2 mb-5">
                {['Avatars premium illimités', 'Chansons IA illimitées', 'Fonds & effets exclusifs', 'Coach voix premium', 'Sans pub'].map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-zinc-200">
                    <Check size={16} className="text-green-400" /> {b}
                  </li>
                ))}
              </ul>
              <Button onClick={() => toast.info('MUZI PASS arrive avec Stripe en Phase 3 🚀')}
                className="btn-neon h-12 px-6 rounded-xl text-white border-0 font-bold">
                <Crown size={18} className="mr-2" /> 9,99€/mois — Bientôt
              </Button>
            </div>
            <div className="text-center hidden md:block"><div className="text-8xl float-soft">👑</div></div>
          </div>
        </div>

        <Tabs defaultValue="effects" className="w-full">
          <TabsList className="glass bg-transparent border border-white/10 rounded-2xl p-1 mb-6 inline-flex h-auto flex-wrap">
            <TabsTrigger value="effects" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold"><Sparkles size={14} className="mr-1.5" /> Effets</TabsTrigger>
            <TabsTrigger value="backgrounds" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold"><ImageIcon size={14} className="mr-1.5" /> Fonds</TabsTrigger>
            <TabsTrigger value="avatars" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold"><Zap size={14} className="mr-1.5" /> Avatars</TabsTrigger>
            <TabsTrigger value="cards" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold"><Music size={14} className="mr-1.5" /> Cartes</TabsTrigger>
            <TabsTrigger value="voices" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-semibold"><Mic size={14} className="mr-1.5" /> Voix</TabsTrigger>
          </TabsList>

          <TabsContent value="effects">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {EFFECT_PACKS.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-4 card-hover relative">
                  {p.popular && <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full">POPULAIRE</div>}
                  <div className={`w-full aspect-square rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center text-6xl mb-3`}>{p.emoji}</div>
                  <div className="font-bold text-white">{p.name}</div>
                  <div className="text-xs text-zinc-400 mb-3">{p.count} effets inclus</div>
                  <Button onClick={() => buy(p, 'effect', p.price)} disabled={owned.includes(p.id) || loadingId === p.id}
                    className="w-full btn-neon text-white border-0 rounded-xl h-9 text-sm font-bold">
                    {loadingId === p.id ? <Loader2 size={14} className="animate-spin" /> : owned.includes(p.id) ? <><Check size={14} className="mr-1" /> Acheté</> : `${p.price}€`}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="backgrounds">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {BACKGROUNDS.map((b) => (
                <div key={b.id} className="glass rounded-2xl overflow-hidden card-hover">
                  <div className="aspect-video relative"><img src={b.img} alt={b.name} className="absolute inset-0 w-full h-full object-cover" /></div>
                  <div className="p-3">
                    <div className="font-bold text-white text-sm">{b.name}</div>
                    <Button onClick={() => buy(b, 'background', b.price)} disabled={owned.includes(b.id) || loadingId === b.id}
                      className="w-full mt-2 btn-neon text-white border-0 rounded-lg h-8 text-xs font-bold">
                      {loadingId === b.id ? <Loader2 size={12} className="animate-spin" /> : owned.includes(b.id) ? <Check size={12} /> : b.price === 0 ? 'Gratuit' : `${b.price}€`}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="avatars">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {AVATARS_PRESET.map((a) => (
                <div key={a.id} className="glass rounded-2xl overflow-hidden card-hover">
                  <div className="aspect-square relative">
                    <img src={a.img} alt={a.name} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 text-2xl">{a.emoji}</div>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-white text-sm truncate">{a.name}</div>
                    <div className="text-[10px] text-zinc-400 uppercase">{a.style}</div>
                    <Button onClick={() => buy(a, 'avatar', a.price || 0)} disabled={owned.includes(a.id) || loadingId === a.id}
                      className="w-full mt-2 btn-neon text-white border-0 rounded-lg h-8 text-xs font-bold">
                      {loadingId === a.id ? <Loader2 size={12} className="animate-spin" /> : owned.includes(a.id) ? <Check size={12} /> : a.locked ? `${a.price}€` : 'Gratuit'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="cards">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {CARD_PACKS.map((c) => (
                <div key={c.id} className="glass rounded-2xl p-5 card-hover">
                  <div className="text-6xl mb-3">{c.emoji}</div>
                  <div className="font-bold text-white">{c.name}</div>
                  <div className="text-xs text-zinc-400 mb-1">{c.count} cartes</div>
                  <div className="text-xs text-zinc-500 mb-3">{c.desc}</div>
                  <Button onClick={() => buy(c, 'card_pack', c.price)} disabled={owned.includes(c.id) || c.price === 0 || loadingId === c.id}
                    className="w-full btn-neon text-white border-0 rounded-xl h-9 text-sm font-bold">
                    {loadingId === c.id ? <Loader2 size={14} className="animate-spin" /> : c.price === 0 ? 'Inclus' : owned.includes(c.id) ? <Check size={14} /> : `${c.price}€`}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="voices">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {COACHES.map((c, i) => (
                <div key={c.id} className="glass rounded-2xl p-5 card-hover text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 flex items-center justify-center text-3xl mb-3 float-soft">{c.emoji}</div>
                  <div className="font-bold text-white text-sm">{c.name}</div>
                  <div className="text-xs text-zinc-400 mb-3">{c.desc}</div>
                  <Button onClick={() => buy(c, 'voice', i < 2 ? 0 : 2.99)} disabled={owned.includes(c.id) || i < 2 || loadingId === c.id}
                    className="w-full btn-neon text-white border-0 rounded-xl h-9 text-xs font-bold">
                    {loadingId === c.id ? <Loader2 size={12} className="animate-spin" /> : i < 2 ? 'Gratuit' : owned.includes(c.id) ? <Check size={12} /> : '2,99€'}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
